// -------------------------------------------------------------------------------------------------------
import * as UtilsGlobals from "./Globals";
import _ from "lodash";
import { saveAs } from "file-saver";
import { isFunction, RunSafe, getUrlParameters, JSONSafeStringify, isString } from "./Micro";
import * as Sentry from "@sentry/react";
import { SeverityLevel } from "@sentry/react";
// -------------------------------------------------------------------------------------------------------
export enum LogLevel {
    Verbose = 0,
    Debug = 1,
    Info = 2,
    Warning = 3,
    Error = 4,
    Critical = 5,
    Exception = 6
}
export interface LogEntry {
    Level: LogLevel;
    Timestamp: Date;
    StackTrace: string[];
    StackError: Error;
    Message?: any[];
}
export interface Log {
    InDebug: boolean;
    Version: string;
    GitVersion: string;
    NodeEnv: string;
    URLParams: string;
    Log: LogEntry[];
    UseSentryIO: boolean;
    MaxMessagesQueueLen: number;
    Timestamp: Date;
}
// -------------------------------------------------------------------------------------------------------
// Build time parameters
declare var __IN_DEBUG__: boolean;
declare var __VERSION__: string;
declare var __GIT_VERSION__: string;
declare var __TIMESTAMP__: string;
declare var process: any;
declare var __USE_REPORTING__: boolean;

(window as any).__LOG_ENABLE_STACK__ = false;
(window as any).__LOG_DISABLE_STACK__ = true;
(window as any).__LOG_LEVEL__ = LogLevel.Debug; // INFO and up is on
var __LOG__: Log = {
    Timestamp: new Date(__TIMESTAMP__),
    InDebug: __IN_DEBUG__,
    Version: __VERSION__,
    GitVersion: __GIT_VERSION__,
    NodeEnv: process.env.NODE_ENV,
    URLParams: window.location.search.substring(1),
    Log: [],
    UseSentryIO: false,
    MaxMessagesQueueLen: 10
};

let __ON_ERROR_EXTERNAL_HANDLER__: OnErrorEventHandler = _.noop;

// Reduce the debug messages in normal operations
if (!__IN_DEBUG__) {
    (window as any).__LOG_LEVEL__ = LogLevel.Warning;
}

// -------------------------------------------------------------------------------------------------------
export class DebugEx {
    // ------------------------------------------------------------------
    static get LogLevel(): LogLevel { return (window as any).__LOG_LEVEL__; }
    static set LogLevel(level: LogLevel) { (window as any).__LOG_LEVEL__ = level; }
    static get LogHistory(): LogEntry[] { return __LOG__.Log; }
    static get Info(): Log { return __LOG__; }
    // ------------------------------------------------------------------
    static get OnError(): OnErrorEventHandler { return __ON_ERROR_EXTERNAL_HANDLER__; }
    static set OnError(handler: OnErrorEventHandler) { __ON_ERROR_EXTERNAL_HANDLER__ = isFunction(handler) ? handler : _.noop; }

    static GetRavenLogLevel(level: LogLevel): SeverityLevel {
        switch (level) {
            case LogLevel.Exception:
                return 'error';
            case LogLevel.Critical:
                return 'fatal';
            case LogLevel.Error:
                return 'error';
            case LogLevel.Warning:
                return 'warning';
            case LogLevel.Info:
                return 'info';
            case LogLevel.Debug:
                return 'debug';
            case LogLevel.Verbose:
                return 'log';
        }
        return 'log';
    }
    // ------------------------------------------------------------------
    static async LogLogin(email: string, domain: string) {
        if (__LOG__.UseSentryIO) {
            Sentry.getCurrentScope().setUser({
                email: email,
                username: email,
                segment: domain,
            });
        }
    }
    // ------------------------------------------------------------------
    private static async Log(printFunc: any, level: LogLevel, externalEx: Error, ...args: any[]) {
        // Use external error
        let ex = externalEx;
        if (ex == null) {
            try {
                throw new Error("Stack");
            }
            catch (localErr) {
                ex = localErr;
            }
        }

        let stack = ex.stack as string;
        let stringifiedStack = isString(stack) ? stack.split("\n") : null;

        // Add maximum number of data
        if (__LOG__.Log.length > __LOG__.MaxMessagesQueueLen)
            __LOG__.Log.shift();

        // Add the log to a global variable
        // TODO: Add max size of this log
        __LOG__.Log.push({
            Level: level,
            Timestamp: new Date(),
            StackTrace: stringifiedStack,
            StackError: ex,
            Message: _.clone(args)
        });

        // Add to the end of the log the stack trace
        args.push(ex);

        if ((window as any).__LOG_LEVEL__ <= level)
            printFunc.apply(this, args);
        else {
            // Create mapping of multiple arguments for extra data
            let dataMap = {};
            for (var i = 1; i < args.length; i++) {
                try {
                    let data = JSON.stringify(args[i]);
                    if (data.length > 1000)
                        data = data.slice(0, 1000);
                    if (data.length > 0 && data != "{}")
                        dataMap["arg-" + i] = data;
                } catch (ex) { }
            }
            // Add as breadcrumb the messages
            Sentry.addBreadcrumb({
                category: this.GetRavenLogLevel(level),
                data: dataMap,
                level: this.GetRavenLogLevel(level),
                message: args[0],
            });
        }

        // Raven support
        if (externalEx != null &&
            __LOG__.UseSentryIO) {
            Sentry.captureException(externalEx);
        }
    }
    // ------------------------------------------------------------------
    public static TraceVerbose(...args: any[]) {
        args.unshift(null);
        args.unshift(LogLevel.Verbose);
        args.unshift(console.log);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceDebug(...args: any[]) {
        args.unshift(null);
        args.unshift(LogLevel.Debug);
        args.unshift(console.debug);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceLog(...args: any[]) {
        args.unshift(null);
        args.unshift(LogLevel.Info);
        args.unshift(console.info);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceWarning(...args: any[]) {
        args.unshift(null);
        args.unshift(LogLevel.Warning);
        args.unshift(console.warn);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceError(...args: any[]) {
        args.unshift(null);
        args.unshift(LogLevel.Error);
        args.unshift(console.error);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceCritical(...args: any[]) {
        args.unshift(null);
        args.unshift(LogLevel.Critical);
        args.unshift(console.error);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceCriticalException(ex: Error, ...args: any[]) {
        args.unshift(ex);
        args.unshift(LogLevel.Critical);
        args.unshift(console.error);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static TraceException(ex: Error, ...args: any[]) {
        // The stack trace resolving take too much time and we make full
        // stack only if we have errors in more than 100ms delay
        args.unshift(ex);
        args.unshift(LogLevel.Exception);
        args.unshift(console.error);
        this.Log.apply(this, args);
    }
    // ------------------------------------------------------------------
    public static DownloadLog() {
        let data = JSONSafeStringify(__LOG__, undefined, "\t");
        saveAs(new Blob([data], { type: "text/plain;charset=utf-8" }), "Log.json");
    }
    // ------------------------------------------------------------------
    public static async LogAppInfo(appName: string) {
        // Get informations from url params
        var params = getUrlParameters();

        // Init Raven Sentry.io
        if (__USE_REPORTING__ === true &&
            !__LOG__.UseSentryIO &&
            (window.location.host.indexOf("localhost") == -1)) {
            // Select project
            let projectId = "https://6469387c87474fedba446b9936d7ae0c@sentry.yodiwo.com/2"; // Project emerald
            if (window.location.hostname == 'tdixons.yodiwo.com' ||
                window.location.hostname == 'dixons.yodiwo.com')
                projectId = "https://30761080f0d34bd9aad52e5c7a26a829@sentry.yodiwo.com/16";

            try {
                Sentry.init({
                    dsn: projectId,
                    release: __LOG__.Version + " - " + __LOG__.GitVersion,
                    environment: __LOG__.NodeEnv,
                    debug: false,
                    integrations: [
                        Sentry.extraErrorDataIntegration({
                            depth: 4,
                        }),
                        Sentry.browserSessionIntegration(),
                        Sentry.browserTracingIntegration(),
                        Sentry.replayIntegration(),
                        Sentry.httpClientIntegration(),
                    ],
                    tracesSampleRate: 0.2, // TODO: define a different value here 
                    attachStacktrace: true,
                    replaysSessionSampleRate: 0,
                    replaysOnErrorSampleRate: 1.0,
                    sendDefaultPii: true,
                    normalizeDepth: 5,
                });
                __LOG__.UseSentryIO = true;
            } catch (ex) {
                console.error(ex);
            }
        }

        console.log("===> Application Starting  <===");
        console.log("App Name       :", appName);
        console.log("__IN_DEBUG__   :", __IN_DEBUG__);
        console.log("__VERSION__    :", __VERSION__);
        console.log("__GIT_VERSION__:", __GIT_VERSION__);
        console.log("__TIMESTAMP__  :", __TIMESTAMP__);
        console.log("NODE_ENV       :", process.env.NODE_ENV);
        console.log("URLParams      :", params);

        // Set the app name
        UtilsGlobals.setGlobal("__APP_NAME__", appName);
        UtilsGlobals.setGlobal("__APP_PARAMS__", params);

        // Update tag in reven with app name
        if (__LOG__.UseSentryIO) {
            let scope = Sentry.getCurrentScope();
            scope.setTag("app_name", appName);
            scope.setTag("git_commit", __LOG__.GitVersion);
            scope.setTag("InDebug", __LOG__.InDebug ? "true" : "false");
            scope.setTag("Domain", window.location.host);
        }
    }
    // ------------------------------------------------------------------
    public static inDev() {
        return process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'prod';
    }
}
// -------------------------------------------------------------------------------------------------------
//Automatically handle errors
window.onerror = function (msg, file, line, col, error) {
    DebugEx.TraceCriticalException(error, msg, file, line, col);

    // trigger event externally
    if (!_.isEqual(DebugEx.OnError, _.noop))
        RunSafe(DebugEx.OnError, msg, file, line, col, error);
};

// -------------------------------------------------------------------------------------------------------
// Link to global variables
(window as any).__LOG__ = __LOG__;
(window as any).__DOWNLOAD_LOG__ = DebugEx.DownloadLog;

// ------------------------------------------------------------------------------------------------------

type AnyFunction = (...args: any[]) => any;

export function SentryAutoSpan<Func extends AnyFunction>(
    name: string,
    fn: Func,
    attributes?: Record<string, any>
): (...args: Parameters<Func>) => ReturnType<Func> {
    const wrappedFn = (...args: Parameters<Func>): ReturnType<Func> => {
        return Sentry.startSpan(
            {
                name: name,
                op: 'func',
                forceTransaction: false,
                onlyIfParent: true,
                attributes: attributes,
            },
            (span) => {
                return fn(...args);
            }
        );
    };
    return wrappedFn;
}

// ------------------------------------------------------------------------------------------------------